/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import * as $ from "jquery";
import {keyCodes, ResultCallback, trivialMatch, TrivialTagComboBox, TrivialTreeBox, wrapWithDefaultTagWrapper} from "trivial-components";

import {UiTagComboBox_WrappingMode, UiTagComboBoxConfig, UiTagComboBoxSubCommandHandler, UiTagComboBoxSubEventSource} from "../../generated/UiTagComboBoxConfig";
import {UiFieldEditingMode} from "../../generated/UiFieldEditingMode";
import {UiTextMatchingMode} from "../../generated/UiTextMatchingMode";
import {UiField} from "./UiField";
import {buildTreeEntryHierarchy, Renderer, TreeEntry} from "../Common";
import {TeamAppsUiContext} from "../TeamAppsUiContext";
import {TeamAppsUiComponentRegistry} from "../TeamAppsUiComponentRegistry";
import {isGridTemplate, isTextCellTemplate} from "../TemplateRegistry";
import {UiTextElementConfig} from "../../generated/UiTextElementConfig";
import {TeamAppsEvent} from "../util/TeamAppsEvent";
import {UiComboBox_LazyChildDataRequestedSubEvent} from "../../generated/UiComboBoxConfig";
import {SubEventFactory} from "../../generated/SubEventFactory";
import {UiTextInputHandlingField_SpecialKeyPressedSubEvent, UiTextInputHandlingField_TextInputSubEvent} from "../../generated/UiTextInputHandlingFieldConfig";
import {UiSpecialKey} from "../../generated/UiSpecialKey";

type Entry = any & { _isFreeTextEntry?: boolean };

/**
 * @author Yann Massard (yamass@gmail.com)
 */
export class UiTagComboBox extends UiField<UiTagComboBoxConfig, any[]> implements UiTagComboBoxSubEventSource, UiTagComboBoxSubCommandHandler {

	public readonly onTextInput: TeamAppsEvent<UiTextInputHandlingField_TextInputSubEvent> = new TeamAppsEvent<UiTextInputHandlingField_TextInputSubEvent>(this, 250);
	public readonly onSpecialKeyPressed: TeamAppsEvent<UiTextInputHandlingField_SpecialKeyPressedSubEvent> = new TeamAppsEvent<UiTextInputHandlingField_SpecialKeyPressedSubEvent>(this, 250);
	public readonly onLazyChildDataRequested: TeamAppsEvent<UiComboBox_LazyChildDataRequestedSubEvent> = new TeamAppsEvent<UiComboBox_LazyChildDataRequestedSubEvent>(this);

	private $originalInput: JQuery;
	private trivialTagComboBox: TrivialTagComboBox<Entry>;
	private templateRenderers: { [name: string]: Renderer };
	private lastResultCallback: Function;

	protected initialize(config: UiTagComboBoxConfig, targetElement: Element, context: TeamAppsUiContext) {
		this.$originalInput = $('<input type="text" autocomplete="off">').appendTo(targetElement);

		this.templateRenderers = context.templateRegistry.createTemplateRenderers(config.templates, null);

		let entries = config.staticData ? buildTreeEntryHierarchy(config.staticData, config.idPropertyName, config.parentIdPropertyName) : null;

		let localQueryFunction: Function;
		if (config.staticData) {
			let trivialMatchingOptions = UiTagComboBox.createTrivialMatchingOptions(config);
			localQueryFunction = (queryString: string, resultCallback: ResultCallback<Entry>) => {
				resultCallback(entries.filter(entry => {
					if (config.matchFieldNames) {
						return config.matchFieldNames.some(fieldName => entry[fieldName] && trivialMatch(entry[fieldName], queryString, trivialMatchingOptions).length > 0);
					} else {
						return Object.keys(entry).some(fieldName => {
							return entry[fieldName] && trivialMatch(entry[fieldName], queryString, trivialMatchingOptions).length > 0
						});
					}
				}));
			}
		}

		let queryFunction = (queryString: string, resultCallback: ResultCallback<Entry>) => {
			this.lastResultCallback = resultCallback;
			this.onTextInput.fire(SubEventFactory.createUiTextInputHandlingField_TextInputSubEvent(queryString));
			if (localQueryFunction != null) {
				localQueryFunction(queryString, resultCallback);
			}
		};

		let emptyEntry: Entry = config.emptyRecord || {};
		let idFunction = (entry: any) => config.idPropertyName ? (entry && entry[config.idPropertyName]) : entry;
		this.trivialTagComboBox = new TrivialTagComboBox<any>(this.$originalInput, {
			entries: entries,
			childrenProperty: "__children",
			expandedProperty: config.expandedProperty,
			showExpanders: config.showExpanders,
			queryFunction: queryFunction,
			entryRenderingFunction: (entry) => {
				let templateName = entry[config.dropDownTemplateProperty] || config.defaultDropDownTemplateName;
				let renderer = this.templateRenderers[templateName] || context.templateRegistry.getTemplateRendererByName(templateName);
				return renderer.render(entry);
			},
			selectedEntryRenderingFunction: (entry) => {
				let templateName;
				if (entry == null) {
					entry = emptyEntry;
					templateName = config.emptyTemplateName || config.defaultDisplayTemplateName;
				} else if (entry && entry[config.displayTemplateProperty]) {
					templateName = entry[config.displayTemplateProperty];
				} else {
					templateName = config.defaultDisplayTemplateName;
				}
				let renderer = this.templateRenderers[templateName] || context.templateRegistry.getTemplateRendererByName(templateName);
				return wrapWithDefaultTagWrapper(renderer.render(entry), config.showClearButton);
			},
			autoComplete: !!config.autoComplete,
			showTrigger: config.showDropDownButton,
			editingMode: config.editingMode === UiFieldEditingMode.READONLY ? 'readonly' : config.editingMode === UiFieldEditingMode.DISABLED ? 'disabled' : 'editable',
			lazyChildrenQueryFunction: (node) => {
				this.onLazyChildDataRequested.fire(SubEventFactory.createUiComboBox_LazyChildDataRequestedSubEvent(node[this._config.idPropertyName]));
			},
			lazyChildrenFlag: entry => !!entry[config.lazyChildrenFlagProperty],
			spinnerTemplate: `<div class="UiSpinner" style="height: 20px; width: 20px; margin: 4px auto 4px auto;"></div>`,
			textHighlightingEntryLimit: config.textHighlightingEntryLimit,
			showDropDownOnResultsOnly: config.showDropDownAfterResultsArrive,
			autoCompleteFunction: (editorText, entry) => {
				if (editorText) {
					if (config.matchFieldNames && config.matchFieldNames.length > 0) {
						for (let i = 0; i < config.matchFieldNames.length; i++) {
							const fieldValue = entry[config.matchFieldNames[i]];
							if (fieldValue && fieldValue.toLowerCase().indexOf(editorText.toLowerCase()) === 0) {
								return fieldValue.toString();
							}
						}
					} else {
						let templateName = entry[config.dropDownTemplateProperty] || config.defaultDropDownTemplateName;
						let renderer = this.getRendererByName(templateName);
						let template = renderer.template;
						if (isTextCellTemplate(template)) {
							let fieldValue = entry[template.textElements[0].fieldName];
							if (fieldValue && fieldValue.toString().toLowerCase().indexOf(editorText.toLowerCase()) === 0) {
								return fieldValue;
							}
						} else if (isGridTemplate(template)) {
							let firstTextElement = template.elements.filter(e => e._type === "UiTextElement")[0] as UiTextElementConfig;
							let fieldValue = entry[firstTextElement.dataKey];
							if (fieldValue && fieldValue.toString().toLowerCase().indexOf(editorText.toLowerCase()) === 0) {
								return fieldValue;
							}
						}
						return "";
					}
				} else {
					if (config.editorTextFields && config.editorTextFields.length > 0) {
						return config.editorTextFields.map(fieldName => entry[fieldName]).filter(value => value != null).join(" ");
					} else {
						let templateName = entry[config.dropDownTemplateProperty] || config.defaultDropDownTemplateName;
						let renderer = this.getRendererByName(templateName);
						let template = renderer.template;
						if (isTextCellTemplate(template)) {
							return entry[template.textElements[0].fieldName];
						} else if (isGridTemplate(template)) {
							let firstTextElement = template.elements.filter(e => e._type === "UiTextElement")[0] as UiTextElementConfig;
							return entry[firstTextElement.dataKey];
						}
					}
				}
			},
			freeTextEntryFactory: (freeText) => {
				return {
					_isFreeTextEntry: true,
					[this._config.freeTextProperty]: freeText
				};
			},
			inputValueFunction: entries => {
				if (entries == null) {
					return "";
				}
				return entries.map((entry: any) => Object.keys(entry).map(key => key + ":" + entry[key]).join(",")).join(";");
			},
			idFunction: idFunction,
			selectionAcceptor: entry => {
				return (!config.distinct || this.trivialTagComboBox.getSelectedEntries().map(idFunction).indexOf(idFunction(entry)) === -1)
					&& (!config.maxEntries || this.trivialTagComboBox.getSelectedEntries().length < config.maxEntries)
					&& (!entry._isFreeTextEntry || config.allowAnyText);
			}
		});
		$(this.trivialTagComboBox.getMainDomElement())
			.addClass("UiTagComboBox")
			.attr("data-fieldname", config.fieldName)
			.toggleClass("wrapping-mode-single-line", config.wrappingMode === UiTagComboBox_WrappingMode.SINGLE_LINE)
			.toggleClass("wrapping-mode-single-tag-per-line", config.wrappingMode === UiTagComboBox_WrappingMode.SINGLE_TAG_PER_LINE);
		this.trivialTagComboBox.onSelectedEntryChanged.addListener(() => this.commit());
		$(this.trivialTagComboBox.getEditor()).on("keydown", (e) => {
			if (e.keyCode === keyCodes.escape) {
				this.onSpecialKeyPressed.fire(SubEventFactory.createUiTextInputHandlingField_SpecialKeyPressedSubEvent(UiSpecialKey.ESCAPE));
			} else if (e.keyCode === keyCodes.enter) {
				this.onSpecialKeyPressed.fire(SubEventFactory.createUiTextInputHandlingField_SpecialKeyPressedSubEvent(UiSpecialKey.ENTER));
			}
		});
	}

	isValidData(v: any[]): boolean {
		return v == null || Array.isArray(v);
	}

	private getRendererByName(templateName: string) {
		return this.templateRenderers[templateName] || this._context.templateRegistry.getTemplateRendererByName(templateName);
	}

	private static createTrivialMatchingOptions(config: UiTagComboBoxConfig) {
		let matchingModes: { [x: number]: 'contains' | 'prefix' | 'prefix-word' | 'prefix-levenshtein' | 'levenshtein' } = {
			[UiTextMatchingMode.PREFIX]: "prefix",
			[UiTextMatchingMode.PREFIX_WORD]: "prefix-word",
			[UiTextMatchingMode.CONTAINS]: "contains",
			[UiTextMatchingMode.SIMILARITY]: "prefix-levenshtein"
		};
		return {
			matchingMode: matchingModes[config.textMatchingMode],
			ignoreCase: true,
			maxLevenshteinDistance: 3
		};
	};

	setDropDownData(data: any[]): void {
		let hierarchicalData = buildTreeEntryHierarchy(data, this._config.idPropertyName, this._config.parentIdPropertyName);
		(this.lastResultCallback || this.trivialTagComboBox.updateEntries.bind(this.trivialTagComboBox))(hierarchicalData);
		if (this._config.showDropDownAfterResultsArrive && data.length > 0 && this.hasFocus()) {
			this.trivialTagComboBox.openDropDown();
		}
	}

	public getMainDomElement(): JQuery {
		return $(this.trivialTagComboBox.getMainDomElement());
	}

	public getFocusableElement(): JQuery {
		return $(this.trivialTagComboBox.getMainDomElement());
	}

	protected displayCommittedValue(): void {
		let uiValue = this.getCommittedValue();
		this.trivialTagComboBox.setSelectedEntries(uiValue);
	}

	public getTransientValue(): any[] {
		const selectedEntries = this.trivialTagComboBox.getSelectedEntries();
		return selectedEntries.map((selectedEntry) => {
			if (selectedEntry._isFreeTextEntry) {
				return {
					_type: this._config.recordTypeId,
					[this._config.freeTextProperty]: selectedEntry[this._config.freeTextProperty]
				};
			} else {
				return selectedEntry;
			}
		});
	}

	focus(): void {
		this.trivialTagComboBox.focus(); // TODO
	}

	public hasFocus(): boolean {
		return this.getMainDomElement().is('.focus');
	}

	protected onEditingModeChanged(editingMode: UiFieldEditingMode): void {
		this.getMainDomElement()
			.removeClass(Object.keys(UiField.editingModeCssClasses).map((key: any) => UiField.editingModeCssClasses[key]).join(" "))
			.addClass(UiField.editingModeCssClasses[editingMode]);
		if (editingMode === UiFieldEditingMode.READONLY) {
			this.trivialTagComboBox.setEditingMode("readonly");
		} else if (editingMode === UiFieldEditingMode.DISABLED) {
			this.trivialTagComboBox.setEditingMode("disabled");
		} else {
			this.trivialTagComboBox.setEditingMode("editable");
		}
	}

	destroy(): void {
		this.trivialTagComboBox.destroy();
		this.$originalInput.remove();
	}


	public static getReadOnlyHtml(config: UiTagComboBoxConfig, value: any[], context: TeamAppsUiContext, drawBorder: boolean): string {
		let content: string;
		if (value) {
			content = value.map((entry) => {
				let templateName = entry[config.displayTemplateProperty] || config.defaultDisplayTemplateName;
				let renderer = config.templates[templateName] ? context.templateRegistry.createTemplateRenderer(config.templates[templateName])
					: context.templateRegistry.getTemplateRendererByName(templateName);
				return wrapWithDefaultTagWrapper(renderer.render(entry));
			}).join("");
		} else {
			content = "";
		}
		return `<div class="static-readonly-UiTagComboBox ${drawBorder ? "bordered" : ""}">${content}</div>`
	}

	getDefaultValue(): any[] {
		return [];
	}

	updateNodes(recordList: any[]): void {
		const topLevelSubNodes = buildTreeEntryHierarchy(recordList, this._config.idPropertyName, this._config.parentIdPropertyName);

		const childrenByParentId: { [parentIdAsString: string]: TreeEntry[] } = {};
		for (let i = 0; i < topLevelSubNodes.length; i++) {
			const subNode = topLevelSubNodes[i];
			let parentIdOrEmptyString = subNode[this._config.parentIdPropertyName] || "";
			if (!childrenByParentId[parentIdOrEmptyString]) {
				childrenByParentId[parentIdOrEmptyString] = [];
			}
			childrenByParentId[parentIdOrEmptyString].push(subNode);
		}

		for (let parentIdAsString in childrenByParentId) {
			let parentId = childrenByParentId[parentIdAsString][0][this._config.parentIdPropertyName];
			(this.trivialTagComboBox.getDropDownComponent() as TrivialTreeBox<Entry>).updateChildren(parentId, childrenByParentId[parentIdAsString]);
		}
	}

	public valuesChanged(v1: any[], v2: any[]): boolean {
		if (v1 == null && v2 == null) {
			return false;
		}
		let nullAndNonNull = ((v1 == null) !== (v2 == null));
		let differentArrayLengths = v1 != null && v2 != null && v1.length !== v2.length;
		if (nullAndNonNull || differentArrayLengths) {
			return true;
		} else {
			for (let i = 0; i < v1.length; i++) {
				if (v1[i][this._config.idPropertyName] !== v2[i][this._config.idPropertyName]
					|| v1[i][this._config.freeTextProperty] !== v2[i][this._config.freeTextProperty]) {
					return true;
				}
			}
			return false;
		}
	}
}

TeamAppsUiComponentRegistry.registerFieldClass("UiTagComboBox", UiTagComboBox);